import { fetchCurrentUser } from '../../ducks/user.duck';
import { SharetribeApi } from '../../util/api';
import { StatusCode } from '../../util/enums';
import { storableError } from '../../util/errors';
import { isArrayLength } from '../../util/genericHelpers';
import { showSuccessToast } from '../../util/toast';
import { parse } from '../../util/urlHelpers';
import { getAssociatedListingId } from '../../util/userDataExtraction';

// ================ Constants ================ //

const RESULT_PAGE_SIZE = 60;

// Action Types
export const ActionTypes = {
  FETCH_TALENT_DETAILS_REQUEST: 'app/LinkTalentPage/FETCH_TALENT_DETAILS_REQUEST',
  FETCH_TALENT_DETAILS_SUCCESS: 'app/LinkTalentPage/FETCH_TALENT_DETAILS_SUCCESS',
  FETCH_TALENT_DETAILS_ERROR: 'app/LinkTalentPage/FETCH_TALENT_DETAILS_ERROR',
  REMOVE_TALENT_REQUEST: 'app/LinkTalentPage/REMOVE_TALENT_REQUEST',
  REMOVE_TALENT_SUCCESS: 'app/LinkTalentPage/REMOVE_TALENT_SUCCESS',
  REMOVE_TALENT_ERROR: 'app/LinkTalentPage/REMOVE_TALENT_ERROR',
  SYNC_TALENT_PROFILE_OWNLIST_REQUEST: 'app/LinkTalentPage/SYNC_TALENT_PROFILE_OWNLIST_REQUEST',
  SYNC_TALENT_PROFILE_OWNLIST_SUCCESS: 'app/LinkTalentPage/SYNC_TALENT_PROFILE_OWNLIST_SUCCESS',
  SYNC_TALENT_PROFILE_OWNLIST_ERROR: 'app/LinkTalentPage/SYNC_TALENT_PROFILE_OWNLIST_ERROR',
};

// ================ Reducer ================ //

const initialState = {
  fetchTalentDetailsInProgress: false,
  fetchTalentDetailsError: null,
  talentDetails: [],
  syncTalentProfileOwnListInProgress: false,
  syncTalentProfileOwnListError: null,
  removeTalentInProgress: false,
  removeTalentError: null,
  selectedTalentId: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ActionTypes.FETCH_TALENT_DETAILS_REQUEST:
      return {
        ...state,
        fetchTalentDetailsInProgress: true,
        fetchTalentDetailsError: null,
        talentDetails: [],
      };
    case ActionTypes.FETCH_TALENT_DETAILS_SUCCESS:
      return {
        ...state,
        fetchTalentDetailsInProgress: false,
        fetchTalentDetailsError: null,
        talentDetails: payload,
      };
    case ActionTypes.FETCH_TALENT_DETAILS_ERROR:
      return {
        ...state,
        fetchTalentDetailsInProgress: false,
        fetchTalentDetailsError: payload,
        talentDetails: [],
      };
    case ActionTypes.SYNC_TALENT_PROFILE_OWNLIST_REQUEST:
      return {
        ...state,
        syncTalentProfileOwnListInProgress: true,
        syncTalentProfileOwnListError: null,
      };
    case ActionTypes.SYNC_TALENT_PROFILE_OWNLIST_SUCCESS:
      return {
        ...state,
        syncTalentProfileOwnListInProgress: false,
        syncTalentProfileOwnListError: null,
      };
    case ActionTypes.SYNC_TALENT_PROFILE_OWNLIST_ERROR:
      return {
        ...state,
        syncTalentProfileOwnListInProgress: false,
        syncTalentProfileOwnListError: payload,
      };

    case ActionTypes.REMOVE_TALENT_REQUEST:
      return {
        ...state,
        removeTalentInProgress: true,
        removeTalentError: null,
        selectedTalentId: payload,
      };
    case ActionTypes.REMOVE_TALENT_SUCCESS:
      return {
        ...state,
        removeTalentInProgress: false,
        removeTalentError: null,
        selectedTalentId: null,
      };
    case ActionTypes.REMOVE_TALENT_ERROR:
      return {
        ...state,
        removeTalentInProgress: false,
        removeTalentError: payload,
        selectedTalentId: null,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const talentDetailsSelector = state => {
  const {
    talentDetails,
    fetchTalentDetailsError,
    fetchTalentDetailsInProgress,
    syncTalentProfileOwnListInProgress,
    syncTalentProfileOwnListError,
  } = state.LinkTalentPage;
  return {
    talentDetails,
    fetchTalentDetailsError,
    fetchTalentDetailsInProgress,
    syncTalentProfileOwnListInProgress,
    syncTalentProfileOwnListError,
  };
};

export const syncTalentInProgressSelector = state => {
  return state.LinkTalentPage.syncTalentProfileOwnListInProgress;
};
export const removeTalentInProgressSelector = state => {
  return state.LinkTalentPage.removeTalentInProgress;
};

export const selectedTalentIdSelector = state => {
  return state.LinkTalentPage.selectedTalentId;
};

// ================ Action Creators ================ //

export const fetchTalentDetailsRequest = () => ({
  type: ActionTypes.FETCH_TALENT_DETAILS_REQUEST,
});

export const fetchTalentDetailsSuccess = data => ({
  type: ActionTypes.FETCH_TALENT_DETAILS_SUCCESS,
  payload: data,
});

export const fetchTalentDetailsError = error => ({
  type: ActionTypes.FETCH_TALENT_DETAILS_ERROR,
  error: true,
  payload: error,
});

export const removeTalentRequest = talentId => ({
  type: ActionTypes.REMOVE_TALENT_REQUEST,
  payload: talentId,
});
export const removeTalentSuccess = () => ({
  type: ActionTypes.REMOVE_TALENT_SUCCESS,
});
export const removeTalentError = error => ({
  type: ActionTypes.REMOVE_TALENT_ERROR,
  error: true,
  payload: error,
});

export const syncTalentProfileOwnListRequest = () => ({
  type: ActionTypes.SYNC_TALENT_PROFILE_OWNLIST_REQUEST,
});

export const syncTalentProfileOwnListSuccess = () => ({
  type: ActionTypes.SYNC_TALENT_PROFILE_OWNLIST_SUCCESS,
});

export const syncTalentProfileOwnListError = error => ({
  type: ActionTypes.SYNC_TALENT_PROFILE_OWNLIST_ERROR,
  error: true,
  payload: error,
});

/**
 * Fetches talent details by email using the Sharetribe API.
 * @param {Object} params - Parameters containing the email.
 * @returns {Promise<Object>} - Talent details or throws an error.
 */
export const fetchTalentDetailsUsingISDK = params => async (dispatch, getState, sdk) => {
  if (!params?.email) {
    const error = new Error('Email is required to fetch talent details');
    dispatch(fetchTalentDetailsError(storableError(error)));
    throw error;
  }

  dispatch(fetchTalentDetailsRequest());

  try {
    const { data = {}, statusCode } = await SharetribeApi.fetchTalentByEmail(params);

    if (statusCode !== StatusCode.SUCCESS) {
      throw new Error(`Failed to fetch talent details: Status ${statusCode}`);
    }
    const user = data;
    const associatedListingId = getAssociatedListingId(user);
    const talentDetails = {
      firstName: user?.attributes?.profile?.firstName || '',
      lastName: user?.attributes?.profile?.lastName || '',
      email: user?.attributes?.email || params.email || '',
      talentId: user?.id?.uuid || '',
      userType: user?.attributes?.profile?.publicData?.userType || '',
      associatedListingId,
    };
    dispatch(fetchTalentDetailsSuccess(talentDetails));
    return talentDetails;
  } catch (error) {
    dispatch(fetchTalentDetailsError(storableError(error)));
    throw error;
  }
};

/**
 * Synchronizes talent profile and own listing data.
 * @param {Object} params - Parameters containing userId and listingId.
 * @returns {Promise} - Resolves when synchronization is complete or throws an error.
 */
export const syncTalentProfileOwnList = params => async (dispatch, getState, sdk) => {
  if (!params?.listingId && !isArrayLength(params?.associatedUsers)) {
    const error = new Error('listing ID are required for synchronization');
    dispatch(syncTalentProfileOwnListError(storableError(error)));
    throw error;
  }

  dispatch(syncTalentProfileOwnListRequest());

  try {
    const response = await SharetribeApi.linkTalentsToListing(params);
    if (response.statusCode !== StatusCode.SUCCESS) {
      throw new Error(`Failed to link talents to listing: Status ${response.statusCode}`);
    }

    dispatch(syncTalentProfileOwnListSuccess());
    dispatch(fetchCurrentUser());
    showSuccessToast('Talents linked successfully');
    return response;
  } catch (error) {
    dispatch(syncTalentProfileOwnListError(storableError(error)));
    throw error;
  }
};

export const removeTalent = params => async (dispatch, getState, sdk) => {
  if (!params?.listingId || !params?.talentId) {
    const error = new Error('Listing ID and Talent ID are required to remove talent');
    dispatch(removeTalentError(storableError(error)));
    throw error;
  }

  dispatch(removeTalentRequest(params?.talentId));

  try {
    const response = await SharetribeApi.unlinkTalentsFromListing(params);
    if (response.statusCode !== StatusCode.SUCCESS) {
      throw new Error(`Failed to remove talent from listing: Status ${response.statusCode}`);
    }

    dispatch(removeTalentSuccess());
    dispatch(fetchCurrentUser());
    showSuccessToast('Talent removed successfully');
    return response;
  } catch (error) {
    dispatch(removeTalentError(storableError(error)));
    throw error;
  }
};

/**
 * Loads initial data for the LinkTalentPage.
 * @param {Object} params - Route parameters.
 * @param {string} search - Query string.
 * @param {Object} config - Configuration object.
 * @returns {Promise} - Resolves when data is loaded.
 */
export const loadData = (params, search, config) => async dispatch => {
  const queryParams = parse(search);
  const { page = 1, ...rest } = queryParams;
  const searchParams = {
    page,
    perPage: RESULT_PAGE_SIZE,
    ...rest,
  };

  return dispatch(fetchCurrentUser());
};
